import React, { useEffect, useState } from "react";
import {
  Table,
  Text,
  Badge,
  Group,
  ActionIcon,
  Box,
  Tooltip,
  Skeleton,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { IconEdit, IconDownload, IconPrinter } from "@tabler/icons-react";
import {
  danger,
  gray,
  primary,
  success,
  warning,
} from "../../../constants/color-constants";
import InfoIcon from "../../../assets/InfoIcon.svg";
import { useMediaQuery } from "@mantine/hooks";
import { useAppSelector } from "../../../store/hooks";
import { querySubmittedStates } from "../../../constants/app-constant";
import { formatSeriesNumber } from "../../../utils/series-number-column";

/** Shrink-wrap serial `#` so auto table layout does not stretch it. */
const SERIES_NUMBER_COL_STYLE: React.CSSProperties = {
  width: "1%",
  whiteSpace: "nowrap",
  textAlign: "center",
  paddingLeft: 8,
  paddingRight: 8,
  boxSizing: "border-box",
  verticalAlign: "top",
};

// Define a comprehensive set of status types
export type StatusType =
  | "Open"
  | "Closed"
  | "Pending"
  | "In Progress"
  | "In-Progress"
  | "Completed"
  | "Query Agreed"
  | "Query Disagreed"
  | "Unable to Determine"
  | "Other"
  | "Unknown Status";

// Interface for the column definition
export interface ColumnDef<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  width?: string;
  align?: "left" | "center" | "right";
  minWidth?: string;
  cellType?: "status" | "aging" | "standard";
  tooltip?: string | ((value: any) => string);
  // New property to specify if column should be fixed (stick to the left)
  fixed?: boolean;
}

// Props for the reusable table
export interface ReusableTableProps<T extends Record<string, any>> {
  data: T[];
  columns: ColumnDef<T>[];
  getRowId?: (row: T) => string;
  showActionButtons?: boolean;
  onEdit?: (row: T) => void;
  onDownload?: (row: T) => void;
  onPrint?: (row: T) => void;
  onRowClick?: (row: T) => void;
  minTableWidth?: string;
  maxTableHeight?: string;
  statusColors?: Record<string, string>;
  canEdit?: (row: T) => boolean;
  isRowClickable?: boolean;
  loading?: boolean;
  downloadingLoading?: string | null;
  printingLoading?: string | null;
  // New prop to show row ID below actions
  showRowIdBelowActions?: boolean;
  // Custom function to get the display text for row ID (optional)
  getRowIdDisplayText?: (row: T) => string;
  // Enhanced height management props
  heightOffset?: number; // Offset from viewport height (for headers, footers, etc.)
  minHeight?: string; // Minimum table height
  useViewportHeight?: boolean; // Whether to use viewport-based height calculation
  isAlert?: boolean;
  rowAlign?: string;
  /** When true, prepends a `#` column with 1-based row numbers (like CDI Alerts). */
  showSeriesNumber?: boolean;
}

// Helper function to render status badges with appropriate colors
const renderStatus = (
  status: StatusType | string,
  statusColors?: Record<string, string>,
  statusId: string | number = "" ////STATUS CHANGES
) => {
  let color: string;
  let displayStatus: string;

  // Normalize the status to handle case variations
  const normalizedStatus = status.toLowerCase().trim();

  // Check if this is a query status that should display as "Completed"
  if (querySubmittedStates.includes(Number(statusId))) {
    ////STATUS CHANGES
    displayStatus = "Completed";
    color = success[7];
  } else {
    // For all other statuses, use the original status
    displayStatus = status;

    // Default colors mapping (case-insensitive)
    const defaultColors: Record<string, string> = {
      open: success[4],
      closed: danger[4],
      pending: warning[4],
      "in-progress": primary[5],
      "in progress": primary[5],
      completed: success[4],
    };

    // Use custom colors if provided, otherwise fall back to defaults
    const colorMap: Record<string, string> = statusColors || defaultColors;

    // Try to find color using normalized status first, then original status
    color = colorMap[normalizedStatus] || colorMap[status] || "gray";
  }

  return (
    <Badge
      color={color}
      variant='light'
      radius='sm'
      size='xs'
      style={{
        border: `1px solid ${color}`,
        height: "20px",
        lineHeight: "22px",
        padding: "0px 4px",
        minWidth: "75px",
        maxWidth: "120px",
        width: "fit-content",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      {displayStatus}
    </Badge>
  );
};

// Helper to render aging with warning icon
const renderAging = (days: number) => {
  if (days <= 0) return "-";

  return (
    <Group
      gap='6px'
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
      }}
    >
      {days > 0 && (
        <Box component='img' height={18} width={18} src={InfoIcon} />
      )}
      <Text size='12px' fw={500} pt={"4px"}>
        {days} d
      </Text>
    </Group>
  );
};

// Helper to render skeleton cell based on column type
const renderSkeletonCell = (column: ColumnDef<any>, rowIndex: number) => {
  const cellType = column.cellType || "standard";

  if (cellType === "status") {
    return (
      <Box style={{ display: "flex", alignItems: "center" }}>
        <Skeleton
          height={30}
          width={85}
          radius='md'
          animate={true}
          style={{
            background:
              "linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)",
            backgroundSize: "200% 100%",
          }}
        />
      </Box>
    );
  }

  if (cellType === "aging") {
    return (
      <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Skeleton height={16} width={16} radius='xs' animate={true} />
        <Skeleton height={18} width={35} radius='sm' animate={true} />
      </Box>
    );
  }

  // For standard cells, create more realistic varied widths based on column and row
  const baseWidths = [75, 95, 115, 135, 155, 120, 140];
  const widthIndex = (column.header.length + rowIndex) % baseWidths.length;
  const baseWidth = baseWidths[widthIndex];
  // Add slight randomness but keep it consistent per cell
  const variation = ((column.header.charCodeAt(0) + rowIndex) % 21) - 10; // -10 to +10
  const finalWidth = Math.max(60, baseWidth + variation);

  return (
    <Box style={{ display: "flex", alignItems: "center" }}>
      <Skeleton
        height={18}
        width={finalWidth}
        radius='sm'
        animate={true}
        style={{
          background:
            "linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)",
          backgroundSize: "200% 100%",
        }}
      />
    </Box>
  );
};

// Helper to render skeleton action buttons with optional row ID
const renderSkeletonActions = (showRowIdBelowActions?: boolean) => {
  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Group gap='xs' justify='flex-start'>
        <Skeleton
          height={34}
          width={34}
          radius='md'
          animate={true}
          style={{
            background:
              "linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)",
            backgroundSize: "200% 100%",
          }}
        />
        <Skeleton
          height={34}
          width={34}
          radius='md'
          animate={true}
          style={{
            background:
              "linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)",
            backgroundSize: "200% 100%",
          }}
        />
      </Group>
      {showRowIdBelowActions && (
        <Skeleton
          height={14}
          width={80}
          radius='sm'
          animate={true}
          style={{
            background:
              "linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)",
            backgroundSize: "200% 100%",
          }}
        />
      )}
    </Box>
  );
};

// Generic table component that can be reused
export function ReusableTable<T extends Record<string, any>>({
  data,
  columns,
  getRowId = (row: T) => row.id?.toString() || "",
  showActionButtons = true,
  onEdit,
  onDownload,
  onPrint,
  onRowClick,
  minTableWidth = "800px",
  maxTableHeight,
  statusColors,
  canEdit = (row: T) => Number(row.statusId) === 2,
  isRowClickable = true,
  loading = false,
  downloadingLoading = null,
  printingLoading = null,
  showRowIdBelowActions = false,
  getRowIdDisplayText,
  heightOffset = 200, // Default offset for headers, navigation, etc.
  minHeight = "400px",
  useViewportHeight = true,
  isAlert = false,
  rowAlign = "middle",
  showSeriesNumber = false,
}: ReusableTableProps<T>) {
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<string>("570px");
  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const provider = useAppSelector(
    (state) => state.provider?.provider?.provider_info
  );

  // Enhanced dynamic height calculation with automatic space detection
  useEffect(() => {
    const calculateDynamicHeight = () => {
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;

      let calculatedHeight: string;

      if (useViewportHeight) {
        // Try to automatically detect available space
        let availableHeight = screenHeight;

        // Try to find the table container's position to calculate actual available space
        const tableContainer =
          document.querySelector("[data-table-container]") ||
          document.querySelector(".table-container") ||
          document.querySelector('[class*="table"]');

        if (tableContainer) {
          const rect = tableContainer.getBoundingClientRect();
          // Calculate space from current position to bottom of screen
          availableHeight = screenHeight - rect.top - 20; // 20px bottom margin
        } else {
          // Fallback: Use aggressive viewport calculation with minimal offset
          availableHeight = screenHeight - Math.min(heightOffset, 100); // Cap offset at 100px max
        }

        // Ensure we use at least 70% of screen on any device, but try for more
        const minAllowedHeight = screenHeight * 0.7;
        availableHeight = Math.max(availableHeight, minAllowedHeight);

        calculatedHeight = `${Math.max(
          parseInt(minHeight),
          availableHeight
        )}px`;
      } else {
        // Even more aggressive fixed calculation - use 85-90% of screen
        let heightPercentage: number;

        if (screenWidth < 768) {
          heightPercentage = 0.85; // 85% on mobile
        } else if (screenWidth < 1440) {
          heightPercentage = 0.88; // 88% on tablet/small desktop
        } else {
          heightPercentage = 0.9; // 90% on large screens
        }

        const aggressiveHeight =
          screenHeight * heightPercentage - Math.min(heightOffset, 80);
        calculatedHeight = `${Math.max(
          parseInt(minHeight),
          aggressiveHeight
        )}px`;
      }

      // Use provided maxTableHeight if it exists, otherwise use calculated
      setDynamicMaxHeight(maxTableHeight || calculatedHeight);
    };

    // Calculate on mount
    calculateDynamicHeight();

    // Debounced resize handler for better performance
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateDynamicHeight, 150);
    };

    window.addEventListener("resize", handleResize);
    // Also recalculate when DOM changes (like when other elements are added/removed)
    const observer = new MutationObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateDynamicHeight, 300);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      clearTimeout(resizeTimeout);
    };
  }, [maxTableHeight, heightOffset, minHeight, useViewportHeight]);

  // Function to get tooltip text for a cell
  const getTooltipText = (row: T, column: ColumnDef<T>): any => {
    // If tooltip is provided in column definition
    if (column.tooltip) {
      if (typeof column.tooltip === "function") {
        // If accessor is a function, we can't reliably get a value for the tooltip function
        if (typeof column.accessor === "function") {
          return "";
        }
        const value = row[column.accessor as keyof T];
        return column.tooltip(value);
      }
      return column.tooltip;
    }

    // Default tooltip is the string representation of the value
    if (typeof column.accessor === "function") {
      return ""; // Can't provide a default for function accessors
    }

    const value = row[column.accessor as keyof T];

    // Handle special cell types
    if (column.cellType === "status" && typeof value === "string") {
      // Normalize the status to handle case variations
      const normalizedStatus = value.toLowerCase().trim();

      // Query statuses that should display as "Completed"
      const queryCompletedStatuses = [
        "query agreed",
        "query disagreed",
        "unable to determine",
        "other",
        "completed",
        "in-progress", // for in-progress status
      ];

      // Return "Completed" for these statuses
      if (queryCompletedStatuses.includes(normalizedStatus)) {
        return (
          <div>
            <div>{provider?.full_name}</div>
            <div>{`${row?.queryUpdatedDate}, ${row?.updatedFormatTime}`}</div>
            <div>{value}</div>
          </div>
        );
      }

      return value; // Return original status for other cases
    }

    if (column.cellType === "aging" && typeof value === "number") {
      // Return empty string for no tooltip when days <= 0
      if (value <= 0) {
        return "";
      }

      // For positive values, show two-line tooltip with date information
      const queryDate = (row as any)?.queryDate || "";
      const createdFormatTime = (row as any)?.createdFormatTime || "";

      return (
        <div>
          <div>{`Sent - ${value} days ago`}</div>
          <div>{`${queryDate}, ${createdFormatTime}`}</div>
        </div>
      );
    } else if (value === "-") {
      return "";
    }

    // Default to string representation
    return String(value);
  };

  // Renders the cell content based on the column type and cell type
  const renderCell = (row: T, column: ColumnDef<T>) => {
    const accessor = column.accessor;
    const cellType = column.cellType || "standard";

    // If accessor is a function, call it with the row
    if (typeof accessor === "function") {
      return accessor(row);
    }

    // Otherwise access the property directly
    const value = row[accessor as keyof T];

    // Special rendering based on cellType
    if (cellType === "status" && typeof value === "string") {
      return renderStatus(value, statusColors, row?.statusId); ////STATUS CHANGES
    }

    if (cellType === "aging" && typeof value === "number") {
      return renderAging(value);
    }

    // For backward compatibility, check field name too
    if (accessor === "status" && typeof value === "string") {
      return renderStatus(value, statusColors);
    }

    if (accessor === "ageing" && typeof value === "number") {
      return renderAging(value);
    }

    // Default rendering - Fixed to display the actual value
    return (
      <Text size={isSmallScreen ? "12px" : "14px"} fw={500} c={gray[8]}>
        {String(value)}
      </Text>
    );
  };

  // Render action buttons with optional row ID below
  const renderActions = (row: T) => {
    const showEdit = onEdit && canEdit(row);
    const rowId = getRowId(row);
    const isDownloading = downloadingLoading === rowId;
    const isPrinting = printingLoading === rowId;

    // Get the display text for row ID
    const rowIdDisplayText = getRowIdDisplayText
      ? getRowIdDisplayText(row)
      : rowId;

    return (
      <Box style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Group gap='xs' justify='flex-start'>
          {showEdit && (
            <Tooltip
              arrowPosition='side'
              arrowOffset={5}
              arrowSize={5}
              arrowRadius={3}
              label='Edit'
              position='bottom-start'
              withArrow
            >
              <ActionIcon
                variant='outline'
                color={primary[5]}
                size={isSmallScreen ? "sm" : "md"}
                p={isSmallScreen ? 2 : 4}
                className='action-icon-hover'
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  onEdit && onEdit(row);
                }}
              >
                <IconEdit color='black' size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip
            arrowPosition='side'
            arrowOffset={5}
            arrowSize={5}
            arrowRadius={3}
            label={"Download"}
            position='bottom-start'
            withArrow
          >
            <ActionIcon
              variant='outline'
              color={primary[5]}
              size={isSmallScreen ? "sm" : "md"}
              p={isSmallScreen ? 2 : 4}
              className='action-icon-hover'
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click event
                if (!isDownloading) {
                  onDownload && onDownload(row);
                }
              }}
              disabled={isDownloading}
              style={{
                cursor: isDownloading ? "not-allowed" : "pointer",
                opacity: isDownloading ? 0.7 : 1,
              }}
            >
              {isDownloading ? (
                <Loader size={16} color={primary[5]} />
              ) : (
                <IconDownload color='black' size={16} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip
            arrowPosition='side'
            arrowOffset={5}
            arrowSize={5}
            arrowRadius={3}
            label={"Print"}
            position='bottom-start'
            withArrow
          >
            <ActionIcon
              variant='outline'
              color={primary[5]}
              size={isSmallScreen ? "sm" : "md"}
              p={isSmallScreen ? 2 : 4}
              className='action-icon-hover'
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click event
                if (!isPrinting) {
                  onPrint && onPrint(row);
                }
              }}
              disabled={isPrinting}
              style={{
                cursor: isPrinting ? "not-allowed" : "pointer",
                opacity: isPrinting ? 0.7 : 1,
              }}
            >
              {isPrinting ? (
                <Loader size={16} color={primary[5]} />
              ) : (
                <IconPrinter color='black' size={16} />
              )}
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Render Row ID below actions if enabled */}
        {showRowIdBelowActions && (
          <Text
            c='dimmed'
            style={{
              fontSize: "12px",
              lineHeight: "14px",
            }}
          >
            {rowIdDisplayText}
          </Text>
        )}
      </Box>
    );
  };

  // Handler for row click
  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Generate skeleton rows (show 6 skeleton rows when loading for better visual)
  const skeletonRows = Array.from({ length: 6 }, (_, index) => (
    <Table.Tr
      key={`skeleton-${index}`}
      className='skeleton-table-row'
      style={{
        backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
        opacity: 1 - index * 0.08, // Subtle fade effect
        transition: "none",
      }}
    >
      {showSeriesNumber && (
        <Table.Td
          style={{
            ...SERIES_NUMBER_COL_STYLE,
            paddingTop: 20,
            paddingBottom: 20,
          }}
        >
          <Skeleton height={14} width={14} radius='sm' mx='auto' />
        </Table.Td>
      )}
      {columns.map((column, cellIndex) => (
        <Table.Td
          key={cellIndex}
          style={{
            textAlign: column.align || "left",
            whiteSpace: "nowrap",
            padding: "20px",
            verticalAlign: "middle",
          }}
        >
          {renderSkeletonCell(column, index)}
        </Table.Td>
      ))}
      {showActionButtons && (
        <Table.Td
          style={{
            whiteSpace: "nowrap",
            padding: "20px",
            verticalAlign: "middle",
          }}
        >
          {renderSkeletonActions(showRowIdBelowActions)}
        </Table.Td>
      )}
    </Table.Tr>
  ));

  const emptyColSpan =
    columns.length + (showActionButtons ? 1 : 0) + (showSeriesNumber ? 1 : 0);

  return (
    <>
      {/* Table Container with Scrollable Body */}
      <Box
        data-table-container
        className='reusable-table-scroll'
        style={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: dynamicMaxHeight,
        }}
      >
        <div style={{ minWidth: minTableWidth }}>
          <Table
            highlightOnHover={!loading}
            highlightOnHoverColor={gray[0]}
            style={{
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            {/* Sticky Header */}
            <Table.Thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: gray[2],
                borderBottom: "2px solid #dee2e6",
              }}
            >
              <Table.Tr>
                {showSeriesNumber && (
                  <Table.Th
                    style={{
                      ...SERIES_NUMBER_COL_STYLE,
                      backgroundColor: gray[2],
                      position: "sticky",
                      top: 0,
                      borderBottom: "2px solid #dee2e6",
                      fontWeight: 600,
                      fontSize: "14px",
                      paddingTop: 6,
                      paddingBottom: 6,
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    #
                  </Table.Th>
                )}
                {columns.map((column, index) => (
                  <Table.Th
                    key={index}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth || "80px",
                      textAlign: column.align || "left",
                      backgroundColor: gray[2],
                      position: "sticky",
                      top: 0,
                      borderBottom: "2px solid #dee2e6",
                      fontWeight: 600,
                      fontSize: "14px",
                      padding: "6px 16px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                      flexWrap: "nowrap",
                    }}
                  >
                    {column.header}
                  </Table.Th>
                ))}
                {showActionButtons && (
                  <Table.Th
                    style={{
                      width: "145px",
                      minWidth: "120px",
                      textAlign: "left",
                      backgroundColor: gray[2],
                      position: "sticky",
                      top: 0,
                      borderBottom: "2px solid #dee2e6",
                      fontWeight: 600,
                      fontSize: "14px",
                      padding: "6px 8px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    Action / Query ID
                  </Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>

            {/* Scrollable Body */}
            <Table.Tbody>
              {loading ? (
                // Show skeleton rows when loading
                skeletonRows
              ) : (
                // Show actual data when not loading
                <>
                  {data?.map((row, index) => (
                    <Table.Tr
                      key={`${getRowId(row)}-${index}`}
                      onClick={() => handleRowClick(row)}
                      style={{
                        transition: "background-color 0.2s ease",
                        backgroundColor: isAlert
                          ? index % 2 === 0
                            ? "#F4F7FF"
                            : "#FFF9F4"
                          : index % 2 === 0
                          ? "#ffffff"
                          : "#fafafa",

                        "&:hover": {
                          backgroundColor: !loading ? gray[0] : undefined,
                        },
                      }}
                    >
                      {showSeriesNumber && (
                        <Table.Td
                          style={{
                            ...SERIES_NUMBER_COL_STYLE,
                            cursor: isRowClickable ? "pointer" : "default",
                            paddingTop: 4,
                            paddingBottom: 4,
                            borderBottom: "1px solid #f1f3f4",
                            verticalAlign: rowAlign ? rowAlign : "top",
                          }}
                          onClick={() =>
                            isRowClickable ? handleRowClick(row) : {}
                          }
                        >
                          <Text
                            fw={500}
                            size='sm'
                            c={gray[8]}
                            ta='center'
                            style={{ lineHeight: 1.35 }}
                          >
                            {formatSeriesNumber(index + 1)}
                          </Text>
                        </Table.Td>
                      )}
                      {columns?.map((column, cellIndex) => {
                        const tooltipText = getTooltipText(row, column);

                        return (
                          <Table.Td
                            key={cellIndex}
                            style={{
                              textAlign: column.align || "left",
                              whiteSpace: "nowrap",
                              cursor: isRowClickable ? "pointer" : "default",
                              padding: "4px 16px",
                              borderBottom: "1px solid #f1f3f4",
                              verticalAlign: rowAlign ? rowAlign : "middle", //
                            }}
                            onClick={() =>
                              isRowClickable ? handleRowClick(row) : {}
                            }
                            className='table-cell-tooltip'
                          >
                            {tooltipText ? (
                              <Tooltip
                                label={tooltipText}
                                position='bottom-start'
                                withArrow
                                multiline
                                arrowPosition='side'
                                arrowOffset={5}
                                arrowSize={5}
                                arrowRadius={3}
                              >
                                <div>{renderCell(row, column)}</div>
                              </Tooltip>
                            ) : (
                              renderCell(row, column)
                            )}
                          </Table.Td>
                        );
                      })}
                      {showActionButtons && (
                        <Table.Td
                          style={{
                            whiteSpace: "nowrap",
                            verticalAlign: "top",
                            padding: "4px 8px",
                            borderBottom: "1px solid #f1f3f4",
                          }}
                        >
                          {renderActions(row)}
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                  {!loading && data?.length === 0 && (
                    <Table.Tr>
                      <Table.Td
                        colSpan={emptyColSpan}
                        style={{
                          textAlign: "center",
                          padding: "24px",
                          borderBottom: "1px solid #f1f3f4",
                        }}
                      >
                        <Text size='md' c='dimmed'>
                          No data available
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </div>
      </Box>
    </>
  );
}
